import React, { useState, useEffect } from 'react';
import { Button, Space, Typography, Spin, Tooltip, Form, AutoComplete, Input, Divider, message } from 'antd';
import { CheckCircleFilled, EditOutlined, UserOutlined, ShopOutlined, PlusOutlined } from '@ant-design/icons';
import { useSecureRequest } from '../hooks/useSecureRequest';
import { useCompany } from '../contexts/CompanyContext';
import { useConfig } from '../contexts/ConfigContext';
import { UnifiedContactModal } from '@ryo/ui';
import { useContact } from '../contexts/ContactContext';

const { Text } = Typography;

interface ValidatedEntityInputProps {
  type: 'USER' | 'PROVIDER' | 'CUSTOMER';
  label: string;
  value?: string | number;
  initialName?: string;
  initialId?: number;
  placeholder?: string;
  onChange: (data: { id: number; name: string; document: string; email?: string; electronic_invoice_email?: string; entity?: any }) => void;
  disabled?: boolean;
  rules?: {required: boolean, message: string}[];
  name?: string;
}

const AutoCompleteRender = ({ menu, type, onOpenModal }: { menu: React.ReactNode; type: 'PROVIDER' | 'CUSTOMER' | 'USER'; onOpenModal?: () => void }) => {
  return (
    <>
      {menu}
      {(type === 'PROVIDER' || type === 'CUSTOMER') && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Space style={{ padding: '0 8px 4px' }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onOpenModal?.()}
              size="small"
              style={{ width: '100%' }}
            >
              Crear {type === 'PROVIDER' ? 'Proveedor' : 'Cliente'}
            </Button>
          </Space>
        </>
      )}
    </>
  );
};

/* Internal component to handle Form.Item binding with custom layout */
const InnerInput = ({ value, onChange, onInternalChange, loading, type, options, onSearch, onSelect, onOpenModal, placeholder, ...props }: any) => {
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <AutoComplete
        {...props}
        style={{ flex: 1 }}
        value={value}
        options={options}
        onSearch={onSearch}
        onSelect={onSelect}
        onChange={(val) => {
          const selectedOption = options.find((opt: any) => opt.value === val);
          if (selectedOption?.hasNoDoc) {
            onChange(undefined);
            onInternalChange(undefined);
          } else {
            onChange(val);
            onInternalChange(val);
          }
        }}
        onFocus={() => {
          if (options.length === 0) {
            onSearch('');
          }
        }}
        notFoundContent={
          loading ? (
             <div style={{ padding: 8, textAlign: 'center' }}><Spin size="small" /></div>
          ) : (
             <div style={{ padding: '8px 12px', color: '#999' }}>No se encontraron resultados</div>
          )
        }
        popupRender={(menu: React.ReactNode) => AutoCompleteRender({ menu, type, onOpenModal })}
      >
        <Input 
          prefix={type === 'USER' ? <UserOutlined /> : <ShopOutlined />} 
          placeholder={placeholder}
        />
      </AutoComplete>
      {loading && <Spin size="small" />}
    </div>
  );
};
function getEndpoint(type:string){
  return type === 'USER' 
        ? '/user/index/get-user-by-id'
        : '/user/contact/get-contact-by-id';
}
const ValidatedEntityInput: React.FC<ValidatedEntityInputProps> = ({
  type,
  label,
  value,
  initialName,
  initialId,
  placeholder,
  onChange: onChangeProp,
  disabled,
  rules,
  name
}) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const { securePost } = useSecureRequest();
  const { contact: contactState, updateContact } = useContact();
  
  const [loading, setLoading] = useState(false);
  const [documentNumber, setDocumentNumber] = useState<string | number | undefined>(value);
  const [entityName, setEntityName] = useState<string | undefined>(initialName);
  const [isValidated, setIsValidated] = useState(!!initialId);
  const [options, setOptions] = useState<{ value: string; label: string; entity: any }[]>([]);

  // Debounce ref
  const searchTimeoutRef = React.useRef<any>(null);

  useEffect(() => {
    if (value !== undefined) setDocumentNumber(value);
    if (initialName !== undefined) setEntityName(initialName);
  }, [value, initialName, initialId]);

  const searchEntities = async (searchText: string = '') => {
    setLoading(true);
    try {
      const endpoint = type === 'USER' 
        ? '/user/index/search-bulk-users'
        : '/user/contact/search-bulk-contacts';

      const payload: any = {
        company: company.current?.uid, 
        search: searchText,
        type: 'light'
      };

      if (type !== 'USER') {
        payload.role = type; 
      }

      const response = await securePost(endpoint, payload);

      if (response.status === 200) {
        const list = type === 'USER' ? response.userList : response.contactList;
        const mapped = (list || []).map((item: any) => {
          const hasNoDoc = !item.document_number || String(item.document_number).trim() === '';
          return {
            value: item.document_number ? String(item.document_number) : `_no_doc_${item.id}`,
            label: hasNoDoc 
              ? `⚠️ Sin documento - ${item.name || item.fullName}` 
              : `${item.name || item.fullName} (${item.document_number || item.id})`,
            entity: item,
            hasNoDoc
          };
        });
        setOptions(mapped);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (company?.current?.uid && !disabled) {
      searchEntities('');
    }
  }, [type, company?.current?.uid, disabled]);

  const handleSearch = (val: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!val) {
      searchEntities(''); 
      return;
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchEntities(val);
    }, 500);
  };

  const handleSelect = (val: string, option: any) => {
    const entity = option.entity;
    
    if (option.hasNoDoc) {
      message.warning('Este contacto no tiene número de documento. Por favor, actualice sus datos.');
      updateContact({ showModal: true, current: entity });
      setDocumentNumber(undefined);
      setEntityName('');
      return;
    }

    const name = entity.name || entity.fullName || (type === 'USER' ? `${entity.firstName || ''} ${entity.lastName || ''}`.trim() : '');
    
    setEntityName(name);
    setDocumentNumber(val); 
    setIsValidated(true);
    setOptions([]); 
    
    onChangeProp({
      id: entity.id,
      name: name,
      document: String(val),
      email: entity.email || entity.electronic_invoice_email || entity.mail || "",
      electronic_invoice_email: entity.electronic_invoice_email || entity.email || "",
      entity: entity
    });
  };

  const handleCreateContactSuccess = (newContact: any) => {
    updateContact({ showModal: false, current: undefined });
    const name = newContact.name || newContact.fullName;
    const documentNumber = newContact.document_number;

    setEntityName(name);
    setDocumentNumber(documentNumber);
    setIsValidated(true);
    setOptions([]);

    onChangeProp({
      id: newContact.id,
      name: name,
      document: String(documentNumber),
      email: newContact.email || newContact.electronic_invoice_email || newContact.mail || "",
      electronic_invoice_email: newContact.electronic_invoice_email || newContact.email || "",
      entity: newContact
    });
  };

  const handleSaveContact = async (values: any) => {
    const isEdit = !!values.uid
    const dataRequest: any = {
      company: company.current?.uid,
      module: config.module,
      contact: { ...values, company_id: company.current?.id }
    }

    let url = "/user/contact/create-contact"
    if (isEdit) {
      dataRequest.uidContact = values.uid
      dataRequest.contact.id = values.id
      url = "/user/contact/update-contact"
    } else {
      dataRequest.contact.roles = [{ role_type: type }]
    }

    const res = await securePost(url, dataRequest)
    if (res.status !== 200) {
      throw new Error(res.message || "Error al guardar el contacto")
    }
    return res.contact;
  };

  const checkEntity = async (_rule: unknown, val: unknown) => {
    const doc = (val as string | number | undefined)?.toString();
    if (!doc) return;
    if (isValidated && doc === documentNumber?.toString()) return;

    setLoading(true);
    try {
      const endpoint = getEndpoint(type);

      const payload = type === 'USER'
        ? { idUser: doc, company: company.current.uid, module: config.module }
        : { idContact: doc, company: company.current.uid, module: config.module };

      const response = await securePost(endpoint, payload);

      if (response.status === 200) {
        const entity = type === 'USER' ? response.user : response.contact;
        if (entity?.id) {
          const entityName = entity.name || entity.fullName || (type === 'USER' ? `${entity.firstName || ''} ${entity.lastName || ''}`.trim() : '');
          setEntityName(entityName);
          setIsValidated(true);
          onChangeProp({
            id: entity.id,
            name: entityName,
            document: String(doc),
            email: entity.email || entity.electronic_invoice_email || entity.mail || "",
            electronic_invoice_email: entity.electronic_invoice_email || entity.email || "",
            entity: entity
          });
          return;
        } else {
          throw new Error('No encontrado');
        }
      } else {
        throw new Error('Error en respuesta');
      }
    } catch (error) {
      console.error(error);
      setIsValidated(false);
      setEntityName(undefined);
      onChangeProp({ id: 0, name: '', document: String(doc) });
      throw new Error('No se encontró el registro');
    } finally {
      setLoading(false);
    }
  };

  const internalRules = [
    ...(rules || []),
    { validator: checkEntity }
  ];

  const handleEdit = () => {
    setIsValidated(false);
    setEntityName('');
    onChangeProp({ id: 0, name: '', document: String(documentNumber) });
  };

  const handleInternalChange = (val: any) => {
    setIsValidated(false);
    onChangeProp({ id: 0, name: '', document: val ? String(val) : '' });
    setDocumentNumber(val);
    if (entityName === undefined) setEntityName(''); 
  };

  return (
    <>
      {isValidated && (
        <div style={{ marginBottom: '24px' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: '8px', fontSize: '12px' }}>{label}</Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: '#f6ffed', 
              border: '1px solid #b7eb8f', 
              padding: '5px 12px', 
              borderRadius: '6px',
              justifyContent: 'space-between'
            }}>
              <Space>
                <CheckCircleFilled style={{ color: '#52c41a' }} />
                <div>
                  <Text strong style={{ display: 'block' }}>{entityName}</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Doc: {documentNumber}</Text>
                </div>
              </Space>
              {!disabled && (
                <Tooltip title="Editar">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<EditOutlined />} 
                    onClick={handleEdit}
                  />
                </Tooltip>
              )}
            </div>
          </Space>
        </div>
      )}
      <div style={{ display: isValidated ? 'none' : 'block' }}>
        <Form.Item
          label={isValidated ? null : label}
          name={name}
          rules={internalRules}
          validateTrigger="onBlur"
        >
          <InnerInput 
            onInternalChange={handleInternalChange}
            loading={loading}
            disabled={disabled}
            type={type}
            placeholder={placeholder}
            options={options}
            onSearch={handleSearch}
            onSelect={handleSelect}
            onOpenModal={() => {
              const docNum = (documentNumber !== undefined && documentNumber !== null)
                ? String(documentNumber)
                : (value !== undefined && value !== null ? String(value) : '');
              const entName = initialName || entityName || '';
              updateContact({
                showModal: true,
                current: {
                  document_number: docNum,
                  name: entName,
                } as any
              });
            }}
          />
        </Form.Item>
      </div>
      {(type === 'PROVIDER' || type === 'CUSTOMER') && (
        <UnifiedContactModal 
          visible={contactState?.showModal || false}
          onCancel={() => updateContact({ showModal: false, current: undefined })}
          onSuccess={handleCreateContactSuccess}
          onSave={handleSaveContact}
          role={type}
          initialValues={contactState?.current}
          validationMode="strict"
        />
      )}
    </>
  );
};

export default ValidatedEntityInput;
